import React, { useState, useCallback } from 'react';
import { deserializeFile, isSerializedFile, SerializedFile, sanitizeFilename } from '../../../client-js/src/utils/file-serializer';
import { EyeIcon, ArrowDownIcon, FileIcon } from '@atlas-forms/ui-components-react';

// Revoking right after click() can race the browser's save and corrupt the download;
// keep the blob URL alive long enough for the download to start.
const BLOB_URL_REVOKE_DELAY_MS = 2000;

export interface FileUploadViewModeProps {
  /** File data - can be SerializedFile or array of SerializedFile */
  value: unknown;

  /** Label to display above files */
  label: string;

  /** Whether component allows multiple files */
  multiple?: boolean;

  /** Theme for styling ('light' or 'dark') */
  theme?: 'light' | 'dark';
}

/**
 * Component to display and download uploaded files in form view mode.
 *
 * Shows files with download buttons, file size, and MIME type.
 * Handles both single files and arrays of files.
 *
 * @example
 * ```tsx
 * <FileUploadViewMode
 *   value={submittedFormData.documents}
 *   label="Uploaded Documents"
 *   multiple={true}
 *   theme="light"
 * />
 * ```
 */
export const FileUploadViewMode: React.FC<FileUploadViewModeProps> = ({
  value,
  label,
  multiple = false,
  theme = 'light',
}) => {
  const [downloadingIndices, setDownloadingIndices] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Normalize value to array of serialized files
  const files: SerializedFile[] = Array.isArray(value)
    ? value.filter(isSerializedFile)
    : isSerializedFile(value)
    ? [value]
    : [];

  // No files to display
  if (files.length === 0) {
    return (
      <div className={`af-file-upload-view af-file-upload-view--${theme}`}>
        <label className="af-file-upload-view__label">{label}</label>
        <div className="af-file-upload-view__empty">
          <span>No files uploaded</span>
        </div>
      </div>
    );
  }

  /**
   * Handle file download by reconstructing the File object and triggering browser download.
   */
  const handleDownload = useCallback(async (file: SerializedFile, index: number) => {
    let url: string | null = null;

    try {
      // ← RELIABILITY: Prevent concurrent downloads for same file (race condition fix)
      if (downloadingIndices.has(index)) {
        console.warn(`[FileUploadViewMode] Download already in progress for file ${index}`);
        return;
      }

      setError(null);

      // Add to downloading set
      setDownloadingIndices(prev => new Set(prev).add(index));

      // Reconstruct File from serialized data
      const reconstructedFile = deserializeFile(file);

      // ← SECURITY: Sanitize filename to prevent path traversal and XSS
      const safeFileName = sanitizeFilename(reconstructedFile.name);

      // Create blob URL and download
      url = URL.createObjectURL(reconstructedFile);
      const link = document.createElement('a');

      // ← SAFETY: Validate link element was created
      if (!link) {
        throw new Error('Failed to create download link');
      }

      link.href = url;
      link.download = safeFileName;  // ← Use sanitized name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[FileUploadViewMode] ❌ Download failed:`, err);
      setError(`Failed to download file: ${message}`);
    } finally {
      // ← MEMORY LEAK FIX: Always revoke URL, even if error occurred
      if (url) {
        setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_REVOKE_DELAY_MS);
      }

      // Remove from downloading set
      setDownloadingIndices(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  }, [downloadingIndices]);

  /**
   * Handle file view by opening it in a new tab.
   */
  const handleView = useCallback(async (file: SerializedFile) => {
    try {
      setError(null);

      // Reconstruct File from serialized data
      const reconstructedFile = deserializeFile(file);

      // ← SAFETY: Validate file was reconstructed
      if (!reconstructedFile) {
        throw new Error('Failed to reconstruct file');
      }

      // Create blob URL and open in new tab
      const url = URL.createObjectURL(reconstructedFile);

      // ← SAFETY: Check window.open succeeded
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        URL.revokeObjectURL(url);
        throw new Error('Failed to open file in new window (popup may be blocked)');
      }

      // Note: URL will be revoked after tab closes, but that's okay
      // For a more robust solution, you might want to keep the blob alive longer
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.error(`[FileUploadViewMode] ❌ View failed:`, err);
      setError(`Failed to view file: ${message}`);
    }
  }, []);

  /**
   * Format bytes to human-readable file size.
   * @private
   */
  const formatFileSize = useCallback((bytes: number): string => {
    if (!bytes || bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  }, []);

  /**
   * Check if file has error metadata
   * @private
   */
  const hasError = useCallback((file: SerializedFile): boolean => {
    return '_error' in file && typeof (file as any)._error === 'string';
  }, []);

  /**
   * Get error message from file metadata
   * @private
   */
  const getFileError = useCallback((file: SerializedFile): string | null => {
    return hasError(file) ? (file as any)._error : null;
  }, [hasError]);

  return (
    <div className={`af-file-upload-view af-file-upload-view--${theme}`}>
      <label className="af-file-upload-view__label">{label}</label>

      {error && (
        <div className="af-file-upload-view__error" role="alert">
          {error}
        </div>
      )}

      <div className="af-file-upload-view__files">
        {files.map((file, index) => {
          const fileError = getFileError(file);
          const isErrorFile = hasError(file);

          return (
            <div key={index} className={`af-file-upload-view__file-item ${isErrorFile ? 'af-file-upload-view__file-item--error' : ''}`}>
              {/* File Info Section */}
              <div className="af-file-upload-view__file-info">
                <div className="af-file-upload-view__file-icon">
                  {isErrorFile ? '⚠️' : <FileIcon />}
                </div>
                <div className="af-file-upload-view__file-details">
                  <span
                    className={`af-file-upload-view__file-name ${isErrorFile ? 'af-file-upload-view__file-name--error' : ''}`}
                    title={isErrorFile ? fileError || 'File error' : file.name}
                  >
                    {file.name}
                  </span>
                  {isErrorFile ? (
                    <span className="af-file-upload-view__file-error-msg" style={{ color: '#d32f2f', fontSize: '12px' }}>
                      ❌ {fileError}
                    </span>
                  ) : (
                    <span className="af-file-upload-view__file-meta">
                      {file.type} • {formatFileSize(file.size)}
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="af-file-upload-view__action-buttons">
                {/* View Button - disabled if error */}
                <button
                  className="af-file-upload-view__action-btn af-file-upload-view__action-btn--view"
                  onClick={() => handleView(file)}
                  disabled={isErrorFile}
                  title={isErrorFile ? 'File has error' : `View ${file.name}`}
                  aria-label={`View ${file.name}`}
                  type="button"
                >
                  <EyeIcon />
                </button>

                {/* Download Button - disabled if error or downloading */}
                <button
                  className="af-file-upload-view__action-btn af-file-upload-view__action-btn--download"
                  onClick={() => handleDownload(file, index)}
                  disabled={isErrorFile || downloadingIndices.has(index)}
                  title={isErrorFile ? 'File has error' : `Download ${file.name}`}
                  aria-label={`Download ${file.name}`}
                  type="button"
                >
                  {downloadingIndices.has(index) ? (
                    <span className="af-file-upload-view__loading-spinner">⏳</span>
                  ) : (
                    <ArrowDownIcon />
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FileUploadViewMode;
